import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import { useTheme } from "@mui/material/styles";
import {
  Card,
  CardHeader,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { useChart } from "../../../components/chart";
import { useGetMembersQuery } from "../../../features/members/membersApiSlice";
import UserRolesBadge from "../../../components/rolesbadge/UserRolesBadge";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const getValidDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const sumValues = (values) =>
  values.reduce((total, current) => total + current, 0);

export default function AppMemberLifecycleTrends({
  title = "Mitgliederbewegungen",
  ...other
}) {
  const theme = useTheme();
  const { data: members = [], isLoading } = useGetMembersQuery();

  const years = useMemo(() => {
    const yearSet = new Set();

    (members || []).forEach((member) => {
      const contractDate = getValidDate(member?.contractDate);
      const terminationDate = getValidDate(member?.terminationDate);

      if (contractDate) yearSet.add(contractDate.getFullYear());
      if (terminationDate) yearSet.add(terminationDate.getFullYear());
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [members]);

  const latestYear = years.length
    ? years[years.length - 1]
    : new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(latestYear);

  useEffect(() => {
    if (!years.length) return;
    if (!years.includes(selectedYear)) {
      setSelectedYear(years[years.length - 1]);
    }
  }, [selectedYear, years]);

  const { signups, terminations } = useMemo(() => {
    const signupsByMonth = Array(12).fill(0);
    const terminationsByMonth = Array(12).fill(0);

    (members || []).forEach((member) => {
      const contractDate = getValidDate(member?.contractDate);
      if (contractDate && contractDate.getFullYear() === selectedYear) {
        signupsByMonth[contractDate.getMonth()] += 1;
      }

      const terminationDate = getValidDate(member?.terminationDate);
      if (terminationDate && terminationDate.getFullYear() === selectedYear) {
        terminationsByMonth[terminationDate.getMonth()] += 1;
      }
    });

    return { signups: signupsByMonth, terminations: terminationsByMonth };
  }, [members, selectedYear]);

  const chartData = useMemo(
    () => [
      {
        name: "Anmeldungen",
        type: "column",
        fill: "solid",
        data: signups,
      },
      {
        name: "Kündigungen",
        type: "line",
        fill: "solid",
        data: terminations,
      },
    ],
    [signups, terminations],
  );

  const chartOptions = useChart({
    colors: [theme.palette.success.main, theme.palette.error.main],
    plotOptions: { bar: { columnWidth: "40%" } },
    fill: { type: chartData.map((item) => item.fill) },
    labels: MONTH_LABELS,
    xaxis: {
      type: "category",
      categories: MONTH_LABELS,
      labels: {
        style: { colors: theme.palette.text.secondary },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => `${Math.round(value)}`,
      },
      title: {
        text: "Mitglieder",
        style: { color: theme.palette.text.secondary },
      },
    },
    stroke: { width: [0, 3] },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => {
          if (typeof value !== "number") return value;
          return `${Math.round(value)} Mitglieder`;
        },
      },
    },
  });

  const minYear = years.length ? years[0] : selectedYear;
  const maxYear = years.length ? years[years.length - 1] : selectedYear;
  const canGoPrev = selectedYear > minYear;
  const canGoNext = selectedYear < maxYear;

  const signupsTotal = sumValues(signups);
  const terminationsTotal = sumValues(terminations);

  return (
    <Card {...other}>
      <CardHeader
        title={title}
        subheader={
          isLoading ? (
            "Lade Daten ..."
          ) : (
            <p style={{ margin: 0 }}>
              Jahr {selectedYear} - Anmeldungen {signupsTotal} - Kündigungen{" "}
              {terminationsTotal} - Nur für{"  "}
              <UserRolesBadge status="Admin" /> sichtbar.
            </p>
          )
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={() => setSelectedYear((year) => year - 1)}
              disabled={!canGoPrev}
              aria-label="Vorheriges Jahr"
            >
              <Iconify icon="eva:chevron-left-fill" />
            </IconButton>
            <Typography variant="subtitle2">{selectedYear}</Typography>
            <IconButton
              size="small"
              onClick={() => setSelectedYear((year) => year + 1)}
              disabled={!canGoNext}
              aria-label="Nächstes Jahr"
            >
              <Iconify icon="eva:chevron-right-fill" />
            </IconButton>
          </Stack>
        }
      />

      <Box sx={{ px: 3, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Diese Übersicht zeigt pro Monat die Anmeldungen und Kündigungen der
          Mitglieder.
        </Typography>
      </Box>

      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart
          type="line"
          series={chartData}
          options={chartOptions}
          height={364}
        />
      </Box>
    </Card>
  );
}

AppMemberLifecycleTrends.propTypes = {
  title: PropTypes.string,
};
